import { ApiClient } from '@center-technologies/shared-api-clients';
import { LocalizationRow } from 'types/schema';

export interface LocalizationQueryParams {
  locale?: string;
  namespace?: string;
}

/**
 * Fetches localization entries from either the /localizations or /localizations/bundle route.
 *
 * @param apiClient - An initialized ApiClient pointed at your Fastify server.
 * @param params - Query parameters: locale, namespace, and optional `bundle` flag.
 *                  If `bundle` is true, returns a nested object; otherwise, an array of rows.
 * @returns An object with success, statusCode, message, and data.
 *          `data` will be LocalizationRow[] when bundle=false, or a nested Record when bundle=true.
 * @throws Error object { success, statusCode, message, response } on failure.
 */
export async function getLocalizations(
  apiClient: ApiClient,
  params: LocalizationQueryParams & { bundle?: boolean } = {}
): Promise<{
  success: boolean;
  statusCode: number;
  message: string;
  data: LocalizationRow[] | Record<string, any>;
}> {
  // Destructure bundle flag from query params
  const { bundle, ...queryParams } = params;

  // Build query string excluding the bundle flag
  const qs = Object.entries(queryParams)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v!)}`)
    .join('&');

  // Choose appropriate endpoint
  const endpoint = bundle ? '/localizations/bundle' : '/localizations';
  const path = `${endpoint}${qs ? `?${qs}` : ''}`;

  try {
    // Fetch data; bundle returns an object, otherwise an array
    const data = await apiClient.get<LocalizationRow[] | Record<string, any>>(path);

    const isArray = Array.isArray(data);
    return {
      success: true,
      statusCode: 200,
      message: isArray && data.length === 0
        ? 'No localization entries found'
        : 'Localizations retrieved successfully',
      data,
    };
  } catch (err: any) {
    const status = err.response?.status ?? 500;
    const errorMsg = err.response?.data?.error ?? err.message ?? 'Failed to fetch localizations';
    throw {
      success: false,
      statusCode: status,
      message: errorMsg,
      response: err.response ?? { status, data: { error: errorMsg } },
    };
  }
}
